/**
 * @fileoverview Constantes del módulo de normalización de coordenadas PTEL
 * @module lib/coordinate/constants
 * 
 * Extraído de coordinateNormalizer.ts v2.12 como parte de TD-002.A.
 * Contiene rangos de validación para Andalucía y patrones de placeholder.
 * 
 * @version 1.0.0
 * @date 2026-01-25
 */

// ============================================================================
// RANGOS DE COORDENADAS ANDALUCÍA
// ============================================================================

/**
 * Rangos válidos para coordenadas en Andalucía
 * 
 * - UTM: Sistema EPSG:25830 (UTM Zona 30N, ETRS89)
 * - GEOGRAFICAS: Grados decimales WGS84
 * - Y_TRUNCADA: Detección de coordenadas Y sin el prefijo "4"
 * 
 * @remarks
 * Estos rangos se usan para:
 * - Validar que coordenadas están dentro de Andalucía
 * - Detectar coordenadas Y truncadas (sin el "4" inicial)
 * - Distinguir entre X e Y cuando están intercambiadas
 */
export const RANGOS_ANDALUCIA = {
  UTM: {
    /** Mínimo X UTM30: ~100km desde meridiano 3°W */
    X_MIN: 100000,
    /** Máximo X UTM30: ~620km desde meridiano 3°W */
    X_MAX: 620000,
    /** Mínimo Y UTM30: ~3980km desde ecuador */
    Y_MIN: 3980000,
    /** Máximo Y UTM30: ~4290km desde ecuador */
    Y_MAX: 4290000,
  },
  GEOGRAFICAS: {
    /** Latitud mínima: sur de Tarifa */
    LAT_MIN: 36.0,
    /** Latitud máxima: norte Sierra Morena */
    LAT_MAX: 38.75,
    /** Longitud mínima: oeste Huelva (Portugal) */
    LON_MIN: -7.55,
    /** Longitud máxima: este Almería (Murcia) */
    LON_MAX: -1.60,
  },
  /**
   * Rangos para detectar Y truncada (sin el "4" inicial)
   * Valores entre 40,000 y 300,000 son sospechosos de truncación
   */
  Y_TRUNCADA: {
    MIN: 40000,
    MAX: 300000,
  },
} as const;

// ============================================================================
// PLACEHOLDERS DE AUSENCIA DE DATOS
// ============================================================================

/**
 * Placeholders textuales que indican ausencia de datos en documentos PTEL
 * 
 * @remarks
 * Detectados en análisis empírico de documentos municipales:
 * - Español: "indicar", "completar", "pendiente", etc.
 * - Abreviaturas: "n/a", "n.d.", "tbd"
 * - Símbolos: "-", "--", "---"
 */
export const PLACEHOLDERS_TEXTO = new Set([
  'indicar',
  'completar',
  'por completar',
  'pendiente',
  'sin datos',
  'sin data',
  'n/a',
  'na',
  'n.a.',
  'n.d.',
  'nd',
  'por definir',
  'desconocido',
  'ninguno',
  'xxx',
  'tbd',
  'todo',
  'ver plano',
  'consultar',
  'a determinar',
  'sin información',
  'sin informacion',
  'no especificada',
  'no especificado',
  'inexistente',
  'inexistentes',
  's/d',
  '-',
  '--',
  '---',
]);

/**
 * Placeholders numéricos que indican ausencia de datos
 * 
 * @remarks
 * Valores comunes usados como "sin datos" en sistemas GIS:
 * - Ceros y negativos: 0, -1
 * - Valores sentinela: 99999, -9999
 * - Secuencias de prueba: 12345, 123456
 */
export const PLACEHOLDERS_NUMERICOS = new Set([
  0,
  1,
  -1,
  99999,
  999999,
  9999999,
  -9999,
  -99999,
  -999999,
  12345,
  123456,
]);

// ============================================================================
// CONSTANTES DE VALIDACIÓN
// ============================================================================

/**
 * Distancia máxima (en metros) al centroide municipal antes de warning
 */
export const DISTANCIA_WARNING_METROS = 15000;

/**
 * Distancia máxima (en metros) al centroide municipal antes de degradar confianza
 */
export const DISTANCIA_CRITICA_METROS = 30000;

/**
 * EPSG por defecto para coordenadas UTM30 ETRS89
 */
export const EPSG_UTM30_ETRS89 = 25830;

/**
 * EPSG para coordenadas geográficas WGS84
 */
export const EPSG_WGS84 = 4326;
